import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Row} from "reactstrap";
import Button from "../../../../../../components/base/button/button";
import {Add, EditOutlined, OpenInNew} from "@mui/icons-material";
import SearchInput from "../../../../../../components/base/input/search_input";
import UpsertSurveyDialog from "../../../../../../components/dialogs/upsert-survey";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import Api from "../../../../../../../core/services/api/api";
import classnames from "classnames";
import {SurveysListDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import IconButton from "../../../../../../components/base/button/icon_button";
import {SvgIcon, Tooltip} from "@mui/material";
import {ReactComponent as RemoveIcon} from "../../../../../../../assets/images/remove.svg";
import useSearch from "../../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../../core/services/date-utils";
import {Link} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../../hooks/use-access-levels";

const dataGridSaveConfig = {
    name: 'SurveyListViewDataGrid',
    version: 1,
}

const SurveyListView = () => {
    const [surveys, setSurveys] = useState([]);
    const [openUpsertDialog, setOpenUpsertDialog] = useState(false);
    const [selectedSurvey, setSelectedSurvey] = useState(null);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('surveys-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canExportSurveys = operatorAccessLevels.packages.clientPlatform.surveys
        && operatorAccessLevels.packages.clientPlatform.exportSurveys;

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.surveyList);
        configureBehavioralTool((tags) => tags.surveyList);
    }, [configureBehavioralTool, configureNotebookTags]);


    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: SurveysListDataGridCellKeys.preview,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.left,
                sortable: false,
                pinnedToggleable: true,
                resizable: false,
                width: 0,
            },
            {
                name: SurveysListDataGridCellKeys.edit,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.left,
                sortable: false,
                pinnedToggleable: true,
                resizable: false,
                width: 0,
            },
            {
                name: SurveysListDataGridCellKeys.delete,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.left,
                sortable: false,
                pinnedToggleable: true,
                resizable: false,
                width: 0,
            },
            {
                title: "Title",
                name: SurveysListDataGridCellKeys.title,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 150,
            },
            {
                title: "Start Date",
                name: SurveysListDataGridCellKeys.startDate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.date,
                sortable: false,
                width: 150,
            },
            {
                title: "End Date",
                name: SurveysListDataGridCellKeys.endDate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 150,
            },
        ], [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });


    /**
     * Searches over surveys in the system by calling the api regarding this action
     @type {(function(UseSearchFunctionArg): Promise<undefined|ApiResponseDs<*>>)}
     */
    const search = useCallback(async (args) => {
        const forApi = args.filters?.keyword;
        const response = await Api.getListOfSurveys(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setSurveys(response?.data);
        }
    }, [isMounted])

    const searchData = useSearch(search)

    /**
     * Handles adding a new survey for the partner:
     * - Opens a dialog for adding a new survey
     */
    const onAddNewSurveyClicked = () => {
        setSelectedSurvey(null);
        setOpenUpsertDialog(true);
    }

    /**
     * Handles searching over list of surveys based on entered keyword:
     * - Sets the keyword as the new keyword query param and sets the pagination information such that initiates another search over list of surveys
     * @param keyword
     */
    const searchOverSurveys = (keyword) => {
        keyword = keyword?.toString()?.trim();
        searchData.setFilters({
            keyword: keyword,
        })
    }

    /**
     * Handles the edition of a survey:
     * - Opens the dialog for editing the survey information
     */
    const onEditClicked = (survey) => {
        setSelectedSurvey(survey);
        setOpenUpsertDialog(true);
    }

    /**
     * Calls the api for removing a survey from the system:
     * - if the api call was successful, removes the survey from the [surveys] in the state
     * @param {object} survey
     */
    const removeSurvey = useCallback((survey) => {
        Api.deleteSurvey(survey.id).then(response => {
            if (!isMounted())
                return;
            if (response?.resultFlag)
                setSurveys(prevState => prevState.filter(e => e.id !== survey.id));
        })
    }, [isMounted])

    /**
     * Handles deletion of a survey:
     * - Opens a confirmation dialog
     * - if user response was confirmation, calls the function for removing the survey
     */
    const showDeleteConfirmation = useCallback((survey) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-2'}>
                <p className={'text-sm text-color-1 mb-4'}>
                    Do you want to delete this survey?
                </p>
            </div>,
            {},
            (decision) => decision && removeSurvey(survey)
        );
    }, [removeSurvey])

    /**
     * Exports visible survey rows to CSV.
     */
    const exportSurveysToCsv = useCallback(async () => ({
        title: `Surveys - ${DateUtils.localNow().format(DateUtils.dateTimeFormat)}`,
        body: surveys.map((survey) => ({
            Title: survey.title ?? '',
            'Start Date': survey.startDateTime
                ? DateUtils.serverToLocal(survey.startDateTime).format(DateUtils.dateFormat)
                : '',
            'End Date': survey.endDateTime
                ? DateUtils.serverToLocal(survey.endDateTime).format(DateUtils.dateFormat)
                : '',
        })),
    }), [surveys]);

    /**
     * Memo version of table row records used in data grid
     * @type {DataGridRow[]}
     */
    const rows = useMemo(() => {
        return surveys?.map(e => ({
            key: e.id,
            data: e,
            cells: {
                [SurveysListDataGridCellKeys.title]: e.title,
                [SurveysListDataGridCellKeys.startDate]: e.startDateTime,
                [SurveysListDataGridCellKeys.endDate]: <>
                    <div
                        {...dataGridCellTextProps}
                        className={classnames({
                            'expired-end-date': DateUtils.server(e.endDateTime).isBefore(DateUtils.serverNow())
                        })}
                    >
                        <p>
                            {!!(e.endDateTime) ? DateUtils.serverToLocal(e.endDateTime).format(DateUtils.dateFormat) : '--'}
                        </p>
                    </div>
                </>,
                [SurveysListDataGridCellKeys.edit]:
                    <Tooltip title={'Edit'}>
                        <IconButton
                            color={'secondary'}
                            onPressed={() => onEditClicked(e)}>
                            <EditOutlined className='icon normal'/>
                        </IconButton>
                    </Tooltip>,
                [SurveysListDataGridCellKeys.preview]:
                    <Tooltip title={'Preview Survey'}>
                        <IconButton
                            color={'secondary'}
                            component={Link}
                            to={{
                                pathname: routeFunctions.userPlatform.surveys.single(e.id),
                                state: {survey: e},
                            }}
                        >
                            <OpenInNew className='icon normal'/>
                        </IconButton>
                    </Tooltip>,
                [SurveysListDataGridCellKeys.delete]:
                    <Tooltip title={'Remove'}>
                        <IconButton
                            onPressed={() => showDeleteConfirmation(e)}
                            color={'paper.error'}
                            className={'remove-icon'}
                        >
                            <SvgIcon component={RemoveIcon} className='icon normal'/>
                        </IconButton>
                    </Tooltip>,
            }
        })) ?? []
    }, [showDeleteConfirmation, surveys])

    return (
        <div className={'surveys-list-view'}>
            <Row className='no-gutters w-100 align-items-start justify-content-center surveys-list-view'>
                <div className={'d-flex flex-row justify-content-between align-items-center w-100 mt-4 mb-4 px-1'}>
                    <h2 className={'text-xl font-weight-semi-bold '}>
                        Surveys
                    </h2>
                    <Button
                        className='justify-content-center align-items-center d-flex'
                        variant={'outlined'}
                        color={'primary'}
                        onClick={onAddNewSurveyClicked}
                    >
                        <Add className='icon small mr-2'/>
                        <p className={'button-label'}>Add a new Survey</p>
                    </Button>
                </div>
            </Row>
            <SearchInput
                loading={searchData.loading}
                autoSearch
                immediateSearch
                initialValue={searchData.filters?.keyword}
                search={searchOverSurveys}
                label={'Search'}
            />
            <div className='w-100 my-5'/>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                columns={dataGridColumns}
                rows={rows}
                hideFooterActions={true}
                visibleRows={rows.length}
                getExportInfo={canExportSurveys ? exportSurveysToCsv : undefined}
                state={{
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                hidePageSize
                hidePagination
            />
            <UpsertSurveyDialog
                setOpen={setOpenUpsertDialog}
                open={openUpsertDialog}
                selectedSurvey={selectedSurvey}
                setSurveys={setSurveys}
            />
        </div>
    );
}

export default SurveyListView;
